import { useEffect, useMemo, useState } from "react";

interface UseAutoSaveOptions<T> {
  data: T;
  onSave: (data: T) => Promise<void>;
  debounceMs?: number;
}

export const useAutoSave = <T,>({
  data,
  onSave,
  debounceMs = 30000,
}: UseAutoSaveOptions<T>) => {
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    const id = setTimeout(async () => {
      setSaveStatus("saving");
      await onSave(data);
      setSaveStatus("saved");
      setLastSaved(new Date());
    }, debounceMs);

    return () => clearTimeout(id);
  }, [data, debounceMs, onSave]);

  return useMemo(() => ({ saveStatus, lastSaved }), [lastSaved, saveStatus]);
};
